<!-- default.vue -->
<script setup lang="ts">
	import type { ContentNavigationItem as NavItem } from '@nuxt/content';
	import { useUserStore } from '~/stores/user';
	import { useCharacterStore } from '~/stores/character';

	const route = useRoute();
	const userStore = useUserStore();
	const characterStore = useCharacterStore();

	// v3: the current doc is published by pages/[...slug].vue (documentDriven
	// and its useContent() global were removed).
	const page = useCurrentDoc();

	// Extract ToC links from page content
	const tocLinks = computed(() => {
		return page.value?.body?.toc?.links ?? [];
	});

	// v3 navigation tree. Pass the extra frontmatter fields the sidebar needs
	// (`redirect` drives the nucleus editor stubs).
	const { data: navigation } = await useAsyncData('navigation', () =>
		queryCollectionNavigation('content', ['redirect', 'description'])
	);

	// Load characters, shared characters, and trash when entering nucleus pages
	const charactersLoaded = ref(false);

	watch(
		() => route.path,
		async (path) => {
			if (path.startsWith('/nucleus') && userStore.isLoggedIn && !charactersLoaded.value) {
				await characterStore.fetchAll();
				await characterStore.fetchSharedCharacters();
				await characterStore.fetchTrash();
				charactersLoaded.value = true;
			}
		},
		{ immediate: true }
	);

	// Build character nav items from the store
	const characterNavItems = computed(() => {
		return characterStore.characters.map((char) => ({
			title: char.name || 'Unnamed',
			lastSaved: char.lastSaved ?? '',
			path: '/nucleus/CharacterEditor',
			characterId: char.id,
		}));
	});

	// Build shared character nav items from character store
	const sharedCharacterNavItems = computed(() => {
		return characterStore.sharedCharacters.map((sc) => ({
			title: `${sc.characterName} (${sc.ownerAlias})`,
			path: '/nucleus/CharacterEditor',
			characterId: sc.characterId,
			shareId: sc.shareId,
			isShared: true,
		}));
	});

	// Build trash nav items
	const trashNavItems = computed(() => {
		return characterStore.trash.map((t) => ({
			title: t.character.name || 'Unnamed',
			path: '/nucleus/CharacterEditor',
			trashId: t.id,
			isTrash: true,
		}));
	});

	// Filter nucleus nav children based on admin status
	const filterNucleusNav = (sectionNav: NavItem): NavItem => {
		let children = sectionNav.children || [];

		if (!userStore.isAdmin) {
			children = children.filter(
				(child: NavItem) =>
					child.path === '/nucleus/pda' ||
					child.path === '/nucleus/character' ||
					child.path === '/nucleus/characterii'
			);
		}

		// Inject characters as children of the "Character Editor" nav item.
		// Always include a "+ New Character" link so the editor is reachable
		// even when the user has no owned characters.
		const charEditorChildren: any[] = [
			{
				title: '+ New Character',
				path: '/nucleus/CharacterEditor',
				isNewCharacter: true,
			},
			...characterNavItems.value,
		];
		if (sharedCharacterNavItems.value.length > 0) {
			charEditorChildren.push({
				title: 'Shared Characters',
				path: '/nucleus/shared-characters',
				children: sharedCharacterNavItems.value,
			});
		}
		if (trashNavItems.value.length > 0) {
			charEditorChildren.push({
				title: 'Disintegrator Bin',
				path: '/nucleus/trash',
				children: trashNavItems.value,
			});
		}

		if (charEditorChildren.length > 0) {
			children = children.map((child: NavItem) => {
				if (child.path === '/nucleus/character') {
					return { ...child, children: charEditorChildren } as NavItem;
				}
				return child;
			});
		}

		return { ...sectionNav, children };
	};

	// Filter navigation based on current route section
	const filteredNavigation = computed(() => {
		if (!navigation.value) return [];

		const currentPath = route.path;

		// Determine the section based on the current path
		let sectionPath = '';
		if (currentPath.startsWith('/galactic-guide-to-the-universe')) {
			sectionPath = '/galactic-guide-to-the-universe';
		} else if (currentPath.startsWith('/gmg')) {
			sectionPath = '/gmg';
		} else if (currentPath.startsWith('/nucleus')) {
			sectionPath = '/nucleus';
		}

		// If we're in a specific section, filter navigation to only show that section
		if (sectionPath) {
			let sectionNav = navigation.value.find((item: NavItem) => item.path === sectionPath);
			if (!sectionNav) return [];

			// Apply admin filtering and character injection for nucleus section
			if (sectionPath === '/nucleus') {
				sectionNav = filterNucleusNav(sectionNav);
			}

			return [sectionNav];
		}

		// For other pages, show all navigation
		return navigation.value;
	});

	const getIcon = computed(() => {
		return page?.value?.glyph ? `${page.value.glyph}` : undefined;
	});

	const leftDrawerWidth = ref(500);
	const rightDrawerWidth = ref(500);
	const leftDrawerOpen = ref(true);
	const rightDrawerManuallyHidden = ref(false);
	const rightDrawerOpen = computed(() => {
		if (rightDrawerManuallyHidden.value) return false;
		// Hide if explicitly set in page meta
		if (route.meta.hideRightDrawer) return false;
		// Hide if there are no TOC links
		if (!tocLinks.value || tocLinks.value.length === 0) return false;
		return true;
	});
	const hasRightContent = computed(() => {
		if (route.meta.hideRightDrawer) return false;
		return tocLinks.value && tocLinks.value.length > 0;
	});
	const toggleRightDrawer = () => {
		rightDrawerManuallyHidden.value = !rightDrawerManuallyHidden.value;
	};

	const activeTocId = ref('');
	// Method to handle the `activeTocId` emitted from the slot component
	const handleActiveTocId = (id: string) => {
		activeTocId.value = id;
		console.log('Default Active ToC ID updated:', activeTocId.value);
	};

	// Track actual drawer widths from the DOM
	const liveLeftWidth = ref(0);
	const liveRightWidth = ref(0);
	let drawerObserver: MutationObserver | null = null;

	function updateDrawerWidths() {
		const leftEl = document.querySelector('.q-drawer--left') as HTMLElement;
		const rightEl = document.querySelector('.q-drawer--right') as HTMLElement;
		liveLeftWidth.value = leftEl ? leftEl.offsetWidth : 0;
		liveRightWidth.value = rightEl ? rightEl.offsetWidth : 0;
	}

	onMounted(() => {
		updateDrawerWidths();
		// Watch for drawer width changes (resize handle)
		drawerObserver = new MutationObserver(updateDrawerWidths);
		const body = document.querySelector('.q-layout') as HTMLElement;
		if (body) {
			drawerObserver.observe(body, { attributes: true, subtree: true, attributeFilter: ['style'] });
		}
	});

	onUnmounted(() => {
		drawerObserver?.disconnect();
	});

	// Also update when drawer open state changes
	watch([() => leftDrawerOpen.value, rightDrawerOpen], () => {
		nextTick(updateDrawerWidths);
	});
</script>

<template>
	<q-layout view="hHh lpR lFr">
		<q-header bordered class="text-white" style="background: #000" height-hint="98">
			<AppHeader />
		</q-header>

		<!-- SideBarLeftNavigation component -->
		<SideBarLeftNavigation
			:navigation="filteredNavigation ?? []"
			:drawerWidth="leftDrawerWidth"
			:leftDrawerOpen="leftDrawerOpen"
		/>

		<q-page-container style="background-color: black;">
			<div class="row full-height">
				<div class="col-12 content-area full-height">
					<button
						class="nav-toggle"
						:style="{ left: leftDrawerOpen ? liveLeftWidth + 'px' : '4px', top: '68px' }"
						@click="leftDrawerOpen = !leftDrawerOpen"
						:title="leftDrawerOpen ? 'Hide menu' : 'Show menu'"
					>
						<span v-if="leftDrawerOpen">&#x25C0;</span>
						<span v-else>&#x2630;</span>
					</button>
					<button
						v-if="hasRightContent"
						class="nav-toggle"
						:style="{ right: rightDrawerOpen ? liveRightWidth + 'px' : '4px', top: '68px' }"
						@click="toggleRightDrawer"
						:title="rightDrawerOpen ? 'Hide contents' : 'Show contents'"
					>
						<span v-if="rightDrawerOpen">&#x25B6;</span>
						<span v-else>&#x2261;</span>
					</button>
					<div v-if="page?.title && !page?.hideTitle" class="row items-center header">
						<div v-if="getIcon !== undefined" style="width: 64px; margin-right: 8px">
							<q-img :src="getIcon" alt="Icon" width="64px" />
						</div>
						<h1>
							{{ page?.title }}
						</h1>
					</div>
					<div v-if="page?.description && !page?.hideDescription" class="disc">{{ page?.description }}</div>
					<slot />
				</div>
			</div>
		</q-page-container>

		<!-- Right Sidebar -->
		<SideBarRightContents :tocLinks="tocLinks" :drawerWidth="rightDrawerWidth" :rightDrawerOpen="rightDrawerOpen" :activeTocId="activeTocId"/>
	</q-layout>
	
	<!-- Appendix Preview Component -->
	<AppendixPreview />

	<!-- Global Table Dialog -->
	<DialogTable />

	<!-- Global Game Dialog (create/edit a Nucleus game). Mounted here so it
	     works from anywhere — the PDA and the mission pages both trigger it. -->
	<DialogGame />
</template>

<style lang="scss" scoped>
	/* Fill the page container's inner space (100vh minus the header padding
	   Quasar applies) instead of a hard 100vh, which always overflowed by
	   the header height and forced a page scrollbar. */
	.full-height {
		height: 100%;
	}

	/* Left Sidebar */
	.sidebar-left {
		background-color: #000000;
		color: white;
		padding: 1rem;
		height: 100vh; /* Full height of the viewport */
		overflow-y: auto; /* Enable vertical scrolling */
	}

	.q-page-container {
		height: 100vh; /* Ensure the content container takes full viewport height */
		overflow-y: auto; /* Enable scrolling if the content is long */
	}

	.sidebar-custom-width {
		width: 600px; /* Set your desired width here */
	}

	.sidebar-left nav ul {
		list-style: none;
		padding: 0;
	}

	.sidebar-left nav ul li {
		margin-bottom: 1rem;
	}

	.sidebar-left nav ul li a {
		color: #fff;
		text-decoration: none;
	}

	.sidebar-left nav ul li a:hover {
		text-decoration: underline;
	}

	/* Content Area */
	.content-area {
		padding: 2rem;
		overflow-y: auto;
		height: 100%;
		background-color: #000000;
		color: white;
		position: relative;
	}

	.nav-toggle {
		position: fixed;
		z-index: 2000;
		background: #0a1418;
		border: 1px solid #00ffea40;
		border-radius: 2px;
		color: #00ffea;
		font-family: 'Share Tech Mono', 'Courier New', monospace;
		font-size: var(--fs-small);
		width: 28px;
		height: 28px;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.2s;
		padding: 0;
		line-height: 1;
	}

	.nav-toggle:hover {
		border-color: #00ffea;
		background: #00ffea10;
		box-shadow: 0 0 8px #00ffea30;
	}

	.header {
		display: flex;
		align-items: center;
		top: 0;
		z-index: 10;
		padding: 15px 0px 0px;
		transition: padding 0.15s linear, background 0.15s linear, border-color 0.15s linear;
		will-change: padding, background;
		border-bottom: 1px solid transparent;

		h1 {
			margin-bottom: 0;
		}

		&--scrolled {
			padding: 15px 30px;
		}
	}

	/* Description Animations */
	.disc {
		opacity: 0.6;
		font-size: 1.6rem;
		border-right: 0.15em solid rgb(21, 255, 0);
		width: 100%;
		/* Cursor blinks for 5s after load (25 x 0.2s), then cursor-off
		   (forwards, delayed 5s) holds the border transparent for good. */
		-webkit-animation: type3 2s steps(40, end), blink 0.2s step-end 25 alternate, cursor-off 1ms linear 5s forwards;
		animation: type3 3s steps(40, end), blink 0.2s step-end 25 alternate, cursor-off 1ms linear 5s forwards;
		white-space: nowrap;
		overflow: hidden;
	}

	@keyframes type3 {
		0% {
			width: 0;
		}
		1% {
			opacity: 0.6;
		}
		100% {
			opacity: 0.6;
			border: none;
		}
	}

	@keyframes blink {
		50% {
			border-color: transparent;
		}
	}

	@-webkit-keyframes blink {
		50% {
			border-color: transparent;
		}
	}

	@keyframes cursor-off {
		to {
			border-color: transparent;
		}
	}

	@-webkit-keyframes cursor-off {
		to {
			border-color: transparent;
		}
	}
</style>
